// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import {
  Shield,
  Key,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Monitor,
  Bot,
  Copy,
  Calendar as CalendarIcon
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { supabase } from '@saas-boilerplate/shared';
import { useAuth } from '@/contexts/auth-context';
import { useWorkspace } from '@/contexts/context';
import {
  buildGoogleCalendarAuthUrl,
  buildMicrosoftCalendarAuthUrl,
  storeCalendarOAuthState,
} from '@/lib/calendar-oauth';

export function SecurityPrivacy() {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const [mfaFactors, setMfaFactors] = useState<{ totp: any[]; phone: any[] }>({ totp: [], phone: [] });
  const [loadingMfa, setLoadingMfa] = useState(true);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaVerifyCode, setMfaVerifyCode] = useState('');
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState<{ factorId: string; qrCode: string; secret: string } | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [calendarConnections, setCalendarConnections] = useState<
    { id: string; provider: 'google' | 'microsoft'; external_account_email: string | null }[]
  >([]);
  const [calendarBusy, setCalendarBusy] = useState(false);

  const hasMfa = (mfaFactors.totp?.length || 0) + (mfaFactors.phone?.length || 0) > 0;
  useEffect(() => {
    loadMfaStatus();
  }, []);

  useEffect(() => {
    if (showMfaSetup) loadMfaStatus();
  }, [showMfaSetup]);

  const loadMfaStatus = async () => {
    setLoadingMfa(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (!error) setMfaFactors(data || { totp: [], phone: [] });
    } catch (e) {
      console.error('Error loading MFA status:', e);
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleEnrollMfa = async () => {
    setMfaEnrolling(true);
    setEnrollData(null);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: `Authenticator app (${Date.now()})`
      });
      if (error) throw error;
      setEnrollData({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start MFA setup');
    } finally {
      setMfaEnrolling(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!enrollData || !mfaVerifyCode.trim()) return;
    setMfaEnrolling(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollData.factorId
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollData.factorId,
        challengeId: challengeData.id,
        code: mfaVerifyCode.trim()
      });
      if (verifyError) throw verifyError;

      toast.success('2FA enabled successfully');
      setShowMfaSetup(false);
      setEnrollData(null);
      setMfaVerifyCode('');
      loadMfaStatus();
    } catch (e: any) {
      toast.error(e?.message || 'Invalid verification code');
    } finally {
      setMfaEnrolling(false);
    }
  };

  const handleUnenrollMfa = async (factorId: string) => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      toast.success('2FA disabled');
      loadMfaStatus();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to disable 2FA');
    }
  };

  // ── AI connectors (MCP) personal opt-in - docs/PRD_MCP_SERVER.md ──
  // Double opt-in: workspace admins enable connectors for the org; this flag is the individual's
  // own switch (set by the OAuth consent page's Allow). The MCP server checks it on every call,
  // so turning it off instantly cuts all connected clients. Disconnect additionally revokes the
  // OAuth grants' refresh tokens (they are sessions), so clients cannot mint new access tokens.
  const [aiConnectorsOn, setAiConnectorsOn] = useState<boolean | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('profiles').select('ai_connectors_enabled').eq('id', user.id).maybeSingle();
      if (!cancelled) setAiConnectorsOn(Boolean(data?.ai_connectors_enabled));
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const setAiConnectors = async (next: boolean) => {
    if (!user?.id || aiBusy) return;
    setAiBusy(true);
    try {
      const { error } = await supabase.from('profiles').update({ ai_connectors_enabled: next }).eq('id', user.id);
      if (error) throw error;
      setAiConnectorsOn(next);
      toast.success(next ? 'AI connectors enabled for your account' : 'AI connectors disabled. Connected clients lose access immediately.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not update AI connectors');
    } finally {
      setAiBusy(false);
    }
  };

  const disconnectAiClients = async () => {
    if (!user?.id || aiBusy) return;
    setAiBusy(true);
    try {
      const { error: flagErr } = await supabase.from('profiles').update({ ai_connectors_enabled: false }).eq('id', user.id);
      if (flagErr) throw flagErr;
      setAiConnectorsOn(false);
      // OAuth grants live as sessions; revoking other sessions kills their refresh tokens.
      const { error } = await supabase.auth.signOut({ scope: 'others' });
      if (error) throw error;
      toast.success('AI clients disconnected. This also signed you out on other devices.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not disconnect AI clients');
    } finally {
      setAiBusy(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    setRevoking(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: 'others' });
      if (error) throw error;
      toast.success('All other sessions have been signed out');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to revoke sessions');
    } finally {
      setRevoking(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim()) {
      toast.error('Enter your current password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!user?.email) {
      toast.error('Unable to verify password for this account');
      return;
    }
    setChangingPassword(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });
      if (signInError) {
        toast.error('Current password is incorrect');
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchCalendarConnections = async () => {
    if (!workspace?.id || !user?.id) return;
    try {
      const { data, error } = await supabase
        .from('calendar_connections')
        .select('id,provider,external_account_email')
        .eq('workspace_id', workspace.id)
        .eq('user_id', user.id);
      if (error) throw error;
      setCalendarConnections((data || []) as any);
    } catch (e) {
      setCalendarConnections([]);
    }
  };

  useEffect(() => {
    void fetchCalendarConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id, user?.id]);

  const startCalendarOAuth = (provider: 'google' | 'microsoft') => {
    if (!workspace?.id) return;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${String(appUrl).replace(/\/$/, '')}/integrations`;
    const state = storeCalendarOAuthState(workspace.id, provider);

    if (provider === 'google') {
      const cid = import.meta.env.VITE_GOOGLE_CALENDAR_CLIENT_ID;
      if (!cid) {
        toast.error('Google OAuth client ID is not configured.');
        return;
      }
      window.location.href = buildGoogleCalendarAuthUrl({ clientId: cid, redirectUri, state });
      return;
    }

    const cid = import.meta.env.VITE_MICROSOFT_CALENDAR_CLIENT_ID;
    if (!cid) {
      toast.error('Microsoft OAuth client ID is not configured.');
      return;
    }
    window.location.href = buildMicrosoftCalendarAuthUrl({ clientId: cid, redirectUri, state });
  };

  const disconnectCalendar = async (connectionId: string) => {
    setCalendarBusy(true);
    try {
      const { error } = await supabase.from('calendar_connections').delete().eq('id', connectionId);
      if (error) throw error;
      toast.success('Calendar disconnected');
      await fetchCalendarConnections();
    } catch (e: any) {
      toast.error('Failed to disconnect calendar', { description: e?.message });
    } finally {
      setCalendarBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1>Security</h1>
        <p className="text-muted-foreground">
          Security settings and access controls.{' '}
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            Privacy Policy
          </a>
        </p>
      </div>

      {/* Security Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <CardTitle>Security Status</CardTitle>
          </div>
          <CardDescription>Overview of your account security</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="font-medium">Password</div>
              <div className="text-sm text-muted-foreground">Account protected</div>
              <Button variant="link" size="sm" className="mt-2" onClick={() => setShowChangePassword(true)}>
                Change password
              </Button>
            </div>
            <div className="text-center p-4 border rounded-lg">
              {loadingMfa ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : hasMfa ? (
                <>
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <div className="font-medium">2FA Enabled</div>
                  <div className="text-sm text-muted-foreground">Account protected</div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                  <div className="font-medium">2FA Disabled</div>
                  <div className="text-sm text-muted-foreground">Recommended to enable</div>
                </>
              )}
            </div>
            <div className="text-center p-4 border rounded-lg">
              <Monitor className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <div className="font-medium">Sessions</div>
              <div className="text-sm text-muted-foreground">Manage active sessions</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Key className="h-5 w-5" />
            <CardTitle>Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>Add an extra layer of security with an authenticator app</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingMfa ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : hasMfa ? (
            <div className="space-y-3">
              <p className="text-sm">You have {mfaFactors.totp?.length || 0} authenticator factor(s) enrolled.</p>
              {mfaFactors.totp?.map((f: any) => (
                <div key={f.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm font-medium">{(f.friendly_name || '').replace(/ \(\d+\)$/, '') || 'Authenticator app'}</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">Remove</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disable 2FA</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove two-factor authentication from your account. You will only need your password to sign in.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleUnenrollMfa(f.id)}>
                          Disable 2FA
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          ) : (
            <Dialog open={showMfaSetup} onOpenChange={setShowMfaSetup}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => setShowMfaSetup(true)} disabled={mfaEnrolling}>
                  Enable 2FA
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
                  <DialogDescription>
                    Scan the QR code with your authenticator app (Google Authenticator, 1Password, Authy, etc.)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {enrollData ? (
                    <>
                      <div className="flex justify-center">
                        <img
                          src={enrollData.qrCode}
                          alt="QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Can&apos;t scan? Enter this code manually:</p>
                        <code className="bg-muted px-2 py-1 rounded text-sm block break-all">{enrollData.secret}</code>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mfa-code">Verification Code</Label>
                        <Input
                          id="mfa-code"
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                          value={mfaVerifyCode}
                          onChange={(e) => setMfaVerifyCode(e.target.value.replace(/\D/g, ''))}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowMfaSetup(false)}>Cancel</Button>
                        <Button onClick={handleVerifyMfa} disabled={mfaEnrolling || mfaVerifyCode.length !== 6}>
                          Enable 2FA
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Click below to generate a QR code. Scan it with your authenticator app, then enter the 6-digit code to verify.
                      </p>
                      <Button onClick={handleEnrollMfa} disabled={mfaEnrolling}>
                        {mfaEnrolling ? 'Generating...' : 'Generate QR Code'}
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Monitor className="h-5 w-5" />
              <CardTitle>Active Sessions</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRevokeAllSessions}
              disabled={revoking}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${revoking ? 'animate-spin' : ''}`} />
              Revoke All Other Sessions
            </Button>
          </div>
          <CardDescription>Sign out from all other devices. You will stay signed in on this device.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-3 border rounded-lg">
            <Monitor className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Current session</span>
                <span className="text-xs bg-muted px-2 py-0.5 rounded">This device</span>
              </div>
              <p className="text-sm text-muted-foreground">You are signed in on this browser</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MCP - personal opt-in, connect URL + disconnect. See docs/PRD_MCP_SERVER.md */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bot className="h-5 w-5" />
              <CardTitle>MCP</CardTitle>
            </div>
            <Switch
              checked={Boolean(aiConnectorsOn)}
              disabled={aiBusy || aiConnectorsOn === null}
              onCheckedChange={(v) => void setAiConnectors(Boolean(v))}
            />
          </div>
          <CardDescription>
            MCP (Model Context Protocol) lets AI connectors like Claude, ChatGPT and Cursor read your
            Upstand meetings, decisions and action items. The switch turns on when you approve a
            connection, works only in workspaces where an admin has allowed MCP, and cuts access for
            every connected client the moment you turn it off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-lg bg-muted/40 p-3">
            <p className="text-xs font-medium text-foreground">Your MCP server URL</p>
            <div className="mt-1 flex items-center gap-2">
              <p className="min-w-0 flex-1 break-all font-mono text-xs text-muted-foreground">
                https://mcp.upstand.ai/mcp
              </p>
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0"
                onClick={() => {
                  void navigator.clipboard.writeText('https://mcp.upstand.ai/mcp');
                  toast.success('MCP server URL copied');
                }}
              >
                <Copy className="mr-1 h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Add it as a remote MCP server in your AI tool and sign in with your Upstand account when
              prompted.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Disconnect all AI clients</p>
              <p className="text-xs text-muted-foreground">
                Turns the switch off and revokes every granted access token. Also signs you out on
                other devices.
              </p>
            </div>
            <Button variant="outline" size="sm" disabled={aiBusy} onClick={() => void disconnectAiClients()}>
              Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Access */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5" />
            <CardTitle>Calendar Access</CardTitle>
          </div>
          <CardDescription>
            Connect Google or Microsoft calendars so Upstand can match meetings and insert the Upstand.ai instruction block.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(['google', 'microsoft'] as const).map((provider) => {
            const row = calendarConnections.find((r) => r.provider === provider);
            const label = provider === 'google' ? 'Google Calendar' : 'Microsoft 365';
            return (
              <div key={provider} className="flex items-start justify-between gap-4 rounded-lg border p-4">
                <div className="min-w-0">
                  <div className="font-medium">{label}</div>
                  <div className="text-sm text-muted-foreground break-all">
                    {row?.external_account_email || (row ? 'Connected' : 'Not connected')}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {row ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={calendarBusy}
                        onClick={() => startCalendarOAuth(provider)}
                      >
                        Reconnect
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={calendarBusy}
                        onClick={() => disconnectCalendar(row.id)}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      disabled={calendarBusy}
                      onClick={() => startCalendarOAuth(provider)}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={(open) => {
        setShowChangePassword(open);
        if (!open) setCurrentPassword('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password, then your new password. It must be at least 8 characters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowChangePassword(false)}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={changingPassword || !currentPassword.trim() || newPassword.length < 8 || newPassword !== confirmPassword}>
                Update Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
